//import external libraries
import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { restoreAuth } from "./actions/authActions";
import { detectBrand } from "./actions/brandActions";
import ProtectedRoute from "./components/ProtectedRoute";
import GoogleAnalytics from "./components/GoogleAnalytics";
import "bootstrap/dist/css/bootstrap.css";
import "jquery/dist/jquery.js";
import "@popperjs/core/dist/cjs/popper.js";
import "bootstrap/dist/js/bootstrap.js";
import "./utils/autofillFix";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import CookieConsent from "react-cookie-consent";

//import internal resources and services
import CreateProject from "./pages/projects/CreateProject";
import EditProject from "./pages/projects/EditProject";
import ProjectItem from "./pages/projects/projectItem/ProjectItem";
import ProjectItemRevision from "./pages/projects/projectItem/ProjectItemRevision";
import Footer from "./pages/components/Footer";
import PrivacyPolicy from "./pages/other/PrivacyPolicy";
import { Provider } from "./utils/hooks/appContext";
import Snackbar from "./pages/components/Snackbar";
import ProjectItemDocumentation from "./pages/projects/pdf/ProjectItemDocumentation";
import Home from "./pages/Home";
import FramelessSelect from "./pages/FramelessSelect";
import AhuType from "./pages/AhuType";
import Navbar from "./pages/components/Navbar";
import ProluxNavbar from "./pages/components/ProluxNavbar";
import Account from "./pages/login/Account";
import Registration from "./pages/registration/Registration";
import EmailConfirmation from "./pages/registration/EmailConfirmation";
import ResetPassword from "./pages/login/ResetPassword";
import ForgotPassword from "./pages/login/ForgotPassword";
import MainForm from "./pages/configurator/MainForm";
import CheckEmailPage from "./pages/other/CheckEmailPage";
import ChooseUnitType from "./pages/configurator/subforms/ChooseUnitType";
import Molijer from "./pages/molier_diagram/Molijer";
import TechCard from "./pages/tecnicalCard/techCard.jsx";
import DehumidifyCalc from "./pages/configurator/DehumidifyCalc";
import ProjectItemRevisionHydra from "./pages/projects/projectItemHydra/ProjectItemRevisionHydra.jsx";
import TechCardProlux from "./New Tech Card/TechCardProlux.jsx";
import ProluxFooter from "./pages/components/ProluxFooter.jsx";
import AdminDashboard from "./pages/adminPanel/AdminDashboard.jsx";
import { themeCtx } from "./utils/hooks/themeContext.jsx";
import ClivetNavbar from "./pages/components/ClivetNavbar";
import clivetTheme from "./styles/ClivetTheme";
import MonitoringSystemRedirect from "./components/MonitoringSystemRedirect";
import { Configurator } from "./pages/configurator/Configurator";

//Styles
import theme from "./styles/TermoventTheme";
import proluxTheme from "./styles/ProluxTheme.js";

//MUI
import { CssBaseline, Link } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { styled, ThemeProvider } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

/** Public auth pages — no app navbar/footer */
const HIDE_APP_CHROME_PATHS = [
  "/register",
  "/email/confirmation",
  "/forgot-password/reset",
  "/password-reset/reset",
];

const shouldHideAppChrome = (pathname) =>
  HIDE_APP_CHROME_PATHS.some((path) => pathname.startsWith(path));

const AppShell = ({ isClivet, isProlux, currentTheme, children }) => {
  const { pathname } = useLocation();
  const hideChrome = shouldHideAppChrome(pathname);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      {!hideChrome &&
        (isClivet ? (
          <ClivetNavbar />
        ) : isProlux ? (
          <ProluxNavbar />
        ) : (
          <Navbar />
        ))}

      <Box sx={{ flex: 1 }}>{children}</Box>

      {!hideChrome &&
        (currentTheme === true ? <ProluxFooter /> : <Footer />)}
    </Box>
  );
};

const useLocalStorage = (storageKey, fallbackState) => {
  const [value, setValue] = useState(
    JSON.parse(localStorage.getItem(storageKey)) ?? fallbackState
  );

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(value));
  }, [value, storageKey]);

  return [value, setValue];
};

function App() {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const { isClivet, isProlux } = useSelector((state) => state.brand);
  const [isOpen, setIsOpen] = useLocalStorage("is-open", false);
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [modalOpen, setModalopen] = useState(false);

  useEffect(() => {
    // Restore auth state from localStorage on app init
    dispatch(restoreAuth());
    // Detect and set brand based on hostname
    dispatch(detectBrand());
  }, [dispatch]);

  const localStorageTheme = localStorage.getItem("proluxTheme");
  const initialTheme = localStorageTheme === "true";

  const [currentTheme, setCurrentTheme] = useState(initialTheme);

  const toggleTheme = () => {
    setCurrentTheme((prevTheme) => {
      const newTheme = !prevTheme;
      localStorage.setItem("proluxTheme", newTheme);
      return newTheme;
    });
  };

  useEffect(() => {
    localStorage.setItem("proluxTheme", currentTheme);
  }, [currentTheme]);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <themeCtx.Provider value={{ currentTheme, toggleTheme }}>
      <ThemeProvider
        theme={isClivet ? clivetTheme : isProlux ? proluxTheme : theme}
      >
        <CssBaseline />
        <Provider value={{ projectId, setProjectId, modalOpen, setModalopen }}>
          <BrowserRouter>
            <GoogleAnalytics />
            <AppShell
              isClivet={isClivet}
              isProlux={isProlux}
              currentTheme={currentTheme}
            >
              <Snackbar />
              <Routes>
                  {/* Public routes - accessible without authentication */}
                  <Route
                    path="/email/confirmation/:id/:token"
                    element={<EmailConfirmation />}
                  />
                  <Route
                    path="/password-reset/reset/:id/:token"
                    element={<ResetPassword />}
                  />
                  <Route path="/checkemail" element={<CheckEmailPage />} />
                  <Route
                    path="/forgot-password/reset/:id/:token"
                    element={<ForgotPassword />}
                  />
                  <Route path="/register" element={<Registration />} />
                  <Route path="/" element={<FramelessSelect />} />

                  {/* Protected routes - require authentication */}
                  <Route
                    path="/home"
                    element={
                      <ProtectedRoute>
                        <Home />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Home />
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route
                    path="/ahu-type/:ahuType"
                    element={
                      <ProtectedRoute>
                        <AhuType />
                      </ProtectedRoute>
                    }
                  /> */}
                  <Route
                    path="/create-project/:projectId/configurator/:unitType"
                    element={
                      <ProtectedRoute>
                        <Configurator />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/create-project/configurator"
                    element={
                      <ProtectedRoute>
                        <MainForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/create-project/choose-unit"
                    element={
                      <ProtectedRoute>
                        <ChooseUnitType />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/project-item/:projectId"
                    element={
                      <ProtectedRoute>
                        <ProjectItem />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/project-item-revision"
                    element={
                      <ProtectedRoute>
                        <ProjectItemRevision />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/create-project"
                    element={
                      <ProtectedRoute>
                        <CreateProject />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/edit"
                    element={
                      <ProtectedRoute>
                        <EditProject />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/monitoring-system"
                    element={
                      <ProtectedRoute>
                        <MonitoringSystemRedirect />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/project-item-documentation"
                    element={
                      <ProtectedRoute>
                        <ProjectItemDocumentation />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account"
                    element={
                      <ProtectedRoute>
                        <Account />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mollier_chart"
                    element={
                      <ProtectedRoute>
                        <Molijer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/technicalCard"
                    element={
                      <ProtectedRoute>
                        <TechCard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hydra"
                    element={
                      <ProtectedRoute>
                        <DehumidifyCalc />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hydra/project-item"
                    element={
                      <ProtectedRoute>
                        <ProjectItemRevisionHydra />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/newTech"
                    element={
                      <ProtectedRoute>
                        <TechCardProlux />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/adminPanel"
                    element={
                      <ProtectedRoute>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch all - redirect to home if logged in, otherwise to "/" which shows LoginModal */}
                  <Route
                    path="*"
                    element={
                      isLoggedIn ? (
                        <Navigate to="/" replace />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
              </Routes>
            </AppShell>

            {/* Cookie Consent i Dialog */}
            {!isOpen && (
              <CookieConsent
                debug={true}
                location="bottom"
                style={{
                  background: (currentTheme === true ? proluxTheme : theme)
                    .palette.primary.main,
                  textAlign: "center",
                  fontSize: "14px",
                }}
                buttonStyle={{
                  color: (currentTheme === true ? proluxTheme : theme).palette
                    .primary.main,
                  background: (currentTheme === true ? proluxTheme : theme)
                    .palette.secondary.main,
                  fontSize: "14px",
                  borderRadius: "40px",
                }}
                buttonText={t("app.understand_btn")}
                expires={365}
              >
                <Box sx={{ display: "grid" }}>
                  <Box sx={{ pb: 2, display: "grid" }}>
                    {t("app.cookies1")}
                    <Link
                      color={
                        (currentTheme === true ? proluxTheme : theme).palette
                          .secondary.main
                      }
                      sx={{
                        textDecoration: "none",
                        fontSize: "14px",
                        cursor: "pointer",
                      }}
                      onClick={handleClickOpen}
                    >
                      {t("app.cookies2")}
                    </Link>{" "}
                    {t("app.cookies3")}
                  </Box>
                  <Button
                    onClick={handleToggle}
                    sx={{
                      color: (currentTheme === true ? proluxTheme : theme)
                        .palette.primary.main,
                      background: (currentTheme === true ? proluxTheme : theme)
                        .palette.secondary.main,
                      fontSize: { xs: "8px", md: "8px", xl: "14px" },
                      borderRadius: "40px",
                      cursor: "pointer",
                      display: "flex",
                    }}
                  >
                    {t("app.accept_btn")}
                  </Button>
                </Box>
              </CookieConsent>
            )}

            <BootstrapDialog onClose={handleClose} open={open}>
              <BootstrapDialogTitle onClose={handleClose}>
                {t("registration.policy")}
              </BootstrapDialogTitle>
              <DialogContent dividers>
                <PrivacyPolicy />
              </DialogContent>
              <DialogActions>
                <Button
                  autoFocus
                  onClick={handleClose}
                  sx={{ background: theme.palette.secondary.main }}
                >
                  {t("registration.agree")}
                </Button>
              </DialogActions>
            </BootstrapDialog>
          </BrowserRouter>
        </Provider>
      </ThemeProvider>
    </themeCtx.Provider>
  );
}

export default App;
